import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Check, FileDown, GitCompareArrows, Share2, ShieldCheck } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { FormError } from '../components/AuthLayout';

import { API } from '../lib/api';

const FEATURES = [
  { Icon: FileDown, title: 'PDF inspection reports', body: 'A report with every finding, its photo, severity and cost, ready to send to a tenant or keep on file.' },
  { Icon: Share2, title: 'Tenant share links', body: 'A private, read-only link to the report. Your tenant opens it without an account, and you can see when it was opened or turn it off.' },
  { Icon: GitCompareArrows, title: 'Move-in and move-out comparison', body: 'Line up the same rooms across inspections so new damage stands apart from what was already there.' },
];

function longDate(iso) {
  return iso ? new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null;
}

export default function UpgradePage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(params.get('canceled') ? 'Checkout was canceled. You have not been charged.' : '');

  useEffect(() => {
    axios.get(`${API}/billing/status`).then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  const price = status ? `$${(status.price_cents / 100).toFixed(0)}` : '$15';

  const startCheckout = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, { origin_url: window.location.origin });
      window.location.assign(data.url);
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't open checkout. Try again in a moment.");
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto flex max-w-[880px] flex-col gap-8" data-testid="upgrade-page">
        <div>
          <p className="m-0 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted">Pilot Pro</p>
          <h1 className="m-0 mt-1 font-display text-[clamp(30px,3.4vw,44px)] font-extrabold leading-[1.1] tracking-[-0.015em]">
            Every Pro feature for a one-time {price}.
          </h1>
          <p className="m-0 mt-3 max-w-[60ch] text-[17px] leading-7 text-ink-muted">
            You're testing RealSpect before launch. Pilot Pro gives you the full product now, and your feedback shapes what ships.
          </p>
        </div>

        {status?.is_pro ? (
          <div className="flex flex-col items-start gap-3 rounded-md border border-line bg-surface p-6" data-testid="pro-active">
            <p className="m-0 inline-flex items-center gap-2 font-display text-[22px] font-bold leading-7">
              <ShieldCheck className="h-6 w-6 text-sev-low" strokeWidth={1.5} aria-hidden="true" />You have Pilot Pro
            </p>
            <p className="m-0 text-ink-muted">
              Purchased {longDate(status.pro_purchased_at)}.{' '}
              {status.pro_expires_at
                ? `Pro runs until ${longDate(status.pro_expires_at)}.`
                : 'Pro runs through public launch plus 12 months.'}
            </p>
            <Button asChild variant="outline"><Link to="/dashboard">Back to dashboard</Link></Button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
            <ul className="m-0 flex list-none flex-col gap-5 p-0">
              {FEATURES.map(({ Icon, title, body }) => (
                <li key={title} className="flex gap-4">
                  <Icon className="mt-1 h-6 w-6 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                  <div>
                    <p className="m-0 font-bold">{title}</p>
                    <p className="m-0 text-ink-muted">{body}</p>
                  </div>
                </li>
              ))}
              <li className="flex gap-4">
                <Check className="mt-1 h-6 w-6 shrink-0 text-sev-low" strokeWidth={2} aria-hidden="true" />
                <div>
                  <p className="m-0 font-bold">Everything that's free stays free</p>
                  <p className="m-0 text-ink-muted">Photo analysis, damage scores and findings work on every account.</p>
                </div>
              </li>
            </ul>

            <div className="flex flex-col gap-4 rounded-md border-2 border-ink bg-surface p-6">
              <p className="m-0 font-display text-5xl font-extrabold leading-none">{price}</p>
              <p className="m-0 font-bold">One-time payment. No subscription.</p>
              <ul className="m-0 flex list-none flex-col gap-2 p-0 text-[15px] leading-[22px] text-ink-muted">
                <li>Pro through public launch, then 12 more months.</li>
                <li>Full refund within {status?.refund_window_days ?? 14} days, no questions asked.</li>
                <li>Secure checkout by Stripe. Your card details never reach RealSpect.</li>
              </ul>
              <FormError>{error}</FormError>
              <Button size="lg" onClick={startCheckout} disabled={loading || status?.checkout_available === false} data-testid="start-checkout">
                {loading ? 'Opening checkout…' : `Get Pilot Pro for ${price}`}
              </Button>
              {status?.checkout_available === false && (
                <p className="m-0 text-sm text-ink-muted">Checkout isn't switched on yet. Check back soon.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
